import { alpha, createTheme } from "@mui/material/styles";
import {
  controlHeight,
  fontStack,
  rem,
  schemes,
  screens,
  typeScale,
  type TypeStyle,
} from "@/theme/tokens";
import type { ThemeMode } from "@/theme/themeChoice";

// The Broker Portal MUI theme, built only from the brk tokens (tokens.ts), for
// the scheme the broker chose (BRK-S245). A professional density on the one
// type scale (BRK-S261; Nav, 2026-09-28): 14px text, 36px controls, 16px field
// text below md so iOS never zooms on focus, 44px targets under a coarse
// pointer, sentence-case buttons, no elevation on buttons, no Paper overlay.

/** A type style as an MUI typography variant: rem, and a unitless line
 *  height, so the browser's text size still scales it. No tracking: MUI's is
 *  tuned for Roboto. */
function variant({ size, lineHeight, weight }: TypeStyle) {
  return {
    fontSize: rem(size),
    lineHeight: lineHeight / size,
    fontWeight: weight,
    letterSpacing: 0,
  };
}

/** MUI's `up("md")` for Tailwind's md, for the overrides that change there. */
const { breakpoints } = createTheme({
  breakpoints: { values: { xs: 0, ...screens } },
});
const mdUp = breakpoints.up("md");

// `primary` is the accent's TEXT role: links, tabs, outlines and focus read it,
// and by night it is the paler indigo. A contained button is the one place MUI
// fills with primary under its contrast text, so it reads the fill role instead,
// under white, exactly as a Tailwind `bg-brk-accent` button does.
export function createBrkTheme(mode: ThemeMode = "light") {
  const t = schemes[mode];
  return createTheme({
    // Tailwind's screens, so `lg` is 1024px here too (MUI's own was 1200).
    breakpoints: { values: { xs: 0, ...screens } },
    palette: {
      mode,
      primary: {
        main: t.accent,
        dark: t["accent-strong"],
        contrastText: mode === "dark" ? t.ink : "#ffffff",
      },
      secondary: {
        main: t["accent-strong"],
        contrastText: mode === "dark" ? t.ink : "#ffffff",
      },
      error: { main: t.danger },
      text: { primary: t.text, secondary: t.dim },
      background: { default: t.bg, paper: t.panel },
      divider: t.border,
      action: {
        hover: alpha(t.accent, mode === "dark" ? 0.1 : 0.06),
        selected: alpha(t.accent, mode === "dark" ? 0.16 : 0.1),
      },
    },
    shape: { borderRadius: 8 },
    // The type scale (tokens.ts): the variants the app's MUI views use.
    typography: {
      fontFamily: fontStack,
      fontSize: 14,
      htmlFontSize: 16,
      h1: variant(typeScale.title),
      h2: variant(typeScale.heading),
      h3: variant(typeScale.subheading),
      h4: variant(typeScale.subheading),
      body1: variant(typeScale.body),
      body2: variant(typeScale.meta),
      subtitle2: { ...variant(typeScale.meta), fontWeight: 500 },
      caption: variant(typeScale.caption),
      button: {
        ...variant(typeScale.body),
        fontWeight: 500,
        textTransform: "none",
      },
    },
    components: {
      // No ripple, so keyboard focus needs its own mark: ButtonBase sets
      // outline: 0, and the ripple was the only one (WCAG 2.4.7, BRK-S266).
      MuiButtonBase: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": {
              outline: `2px solid ${t.accent}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: controlHeight.buttonMd,
            // A touch screen gets a full-size target, whatever the size.
            "@media (pointer: coarse)": { minHeight: controlHeight.touch },
            variants: [
              {
                props: { variant: "contained", color: "primary" },
                style: {
                  backgroundColor: t["accent-fill"],
                  color: "#ffffff",
                  "&:hover": { backgroundColor: t["accent-strong-fill"] },
                },
              },
            ],
          },
          sizeSmall: { minHeight: controlHeight.buttonSm },
        },
      },
      MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      // A modal's backdrop is the brand ink, not MUI's pure black; a menu's
      // invisible backdrop stays invisible.
      MuiBackdrop: {
        styleOverrides: {
          root: {
            variants: [
              {
                props: { invisible: false },
                style: {
                  backgroundColor: alpha(t.ink, mode === "dark" ? 0.7 : 0.5),
                },
              },
            ],
          },
        },
      },
      // Tabs, checkboxes and clickable chips are 44px targets under a coarse
      // pointer, like the buttons (BRK-S264).
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: controlHeight.tab,
            "@media (pointer: coarse)": { minHeight: controlHeight.touch },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: { "@media (pointer: coarse)": { padding: 12 } },
        },
      },
      MuiTab: {
        styleOverrides: {
          // No block padding: MUI's 12px would make a tab 42px, and the
          // 40px minimum centres the label on its own (BRK-S263).
          root: {
            textTransform: "none",
            fontWeight: 500,
            minHeight: controlHeight.tab,
            paddingTop: 0,
            paddingBottom: 0,
            "@media (pointer: coarse)": { minHeight: controlHeight.touch },
          },
        },
      },
      // Field text is 16px below md, where iOS zooms into anything smaller,
      // and body from md. A one-line field is one line box tall (MUI's is
      // 1.4375em), so with the small field's 8px padding it is 40px below md
      // and 36px from md. Textareas keep MUI's auto height.
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: "1rem",
            lineHeight: "1.5rem",
            [mdUp]: {
              fontSize: rem(typeScale.body.size),
              lineHeight: rem(typeScale.body.lineHeight),
            },
            variants: [
              {
                props: { multiline: false },
                style: {
                  height: "1.5rem",
                  [mdUp]: { height: rem(typeScale.body.lineHeight) },
                },
              },
            ],
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: t.border },
          input: {
            variants: [
              {
                props: { size: "small", multiline: false },
                style: { paddingTop: 8, paddingBottom: 8 },
              },
            ],
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: { ...variant(typeScale.meta), fontWeight: 500 },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: variant(typeScale.caption) },
      },
      // Cells read body, not MUI's body2 (now meta); headers are sentence
      // case, in caption at 500 and the dim colour.
      MuiTableCell: {
        styleOverrides: {
          root: variant(typeScale.body),
          head: {
            ...variant(typeScale.caption),
            fontWeight: 500,
            color: t.dim,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          label: variant(typeScale.caption),
          root: {
            variants: [
              {
                props: { clickable: true },
                style: {
                  "@media (pointer: coarse)": { height: controlHeight.touch },
                },
              },
            ],
          },
        },
      },
      MuiMenuItem: { styleOverrides: { root: variant(typeScale.body) } },
      // Alerts read MUI's body2, which is meta now; a message is body.
      MuiAlert: { styleOverrides: { root: variant(typeScale.body) } },
    },
  });
}
